/**
 * The RPG reducers.
 *
 * Same contract as `session.ts`: pure functions, `now` always injected, one
 * serialisable profile in and out. Two decisions from the plan
 * (docs/rpg-implementation-plan.md §0.2–0.4) shape everything here:
 *
 * - **Game XP is not mastery** (docs/rpg.md §46). These reducers never touch
 *   `concepts`, `attempts`, `streak`, `daily` or `stats` — the academic record
 *   is not the RPG's to write. The only field shared with the rest of the
 *   platform is `xp`, because the game level *is* the existing level.
 * - **Derived progress, claimed rewards.** Quest and boss progress is computed
 *   from definitions and the profile, never stored. What is stored is choices
 *   (character, accepted quests, worn cosmetics) and settlements (rewards
 *   paid, bosses beaten), settled idempotently the way `settleQuests` is: an
 *   id already in a claimed list is never paid twice.
 *
 * `classId` is a costume (docs/rpg.md §10). Nothing may read it to change
 * difficulty, recommendations, XP or access.
 */

import type {
  AvatarConfig,
  BossAnswer,
  BossBattleRecord,
  LearnerProfile,
  RpgClassId,
  RpgState,
} from './types.ts';

/** The empty slice, written the first time any RPG reducer runs. */
export function createRpgState(): RpgState {
  return { gold: 0, questLog: {}, claimedQuestIds: [], clearedBossIds: [], inventory: [], equipped: {} };
}

const rpgOf = (profile: LearnerProfile): RpgState => profile.rpg ?? createRpgState();

export function createCharacter(
  profile: LearnerProfile,
  input: { classId: RpgClassId; avatar: AvatarConfig; now: number },
): LearnerProfile {
  const rpg = rpgOf(profile);
  return {
    ...profile,
    rpg: {
      ...rpg,
      character: {
        classId: input.classId,
        avatar: input.avatar,
        // Running creation again is an edit — a change of heart about the
        // class keeps the character's original birthday.
        createdAt: rpg.character?.createdAt ?? input.now,
        updatedAt: input.now,
      },
    },
  };
}

/** Changes how the character looks. A profile with no character has nothing to dress. */
export function updateAvatar(
  profile: LearnerProfile,
  input: { avatar: AvatarConfig; now: number },
): LearnerProfile {
  const rpg = rpgOf(profile);
  if (!rpg.character) return profile;
  return {
    ...profile,
    rpg: { ...rpg, character: { ...rpg.character, avatar: input.avatar, updatedAt: input.now } },
  };
}

/** Accepts a quest. A no-op once accepted, so `acceptedAt` never moves. */
export function acceptQuest(profile: LearnerProfile, input: { questId: string; now: number }): LearnerProfile {
  const rpg = rpgOf(profile);
  if (rpg.questLog[input.questId]) return profile;
  return {
    ...profile,
    rpg: { ...rpg, questLog: { ...rpg.questLog, [input.questId]: { acceptedAt: input.now } } },
  };
}

/**
 * Pays a quest's reward, once.
 *
 * Idempotent by `claimedQuestIds` — the same construction as `settleQuests` —
 * so a retry, a re-render or a merge cannot double-pay. XP flows through the
 * ordinary `xp` field so `levelForXp` and everything reading it keep working.
 */
export function claimQuestReward(
  profile: LearnerProfile,
  input: { questId: string; xp: number; gold: number },
): LearnerProfile {
  const rpg = rpgOf(profile);
  if (rpg.claimedQuestIds.includes(input.questId)) return profile;
  return {
    ...profile,
    xp: profile.xp + input.xp,
    rpg: {
      ...rpg,
      gold: rpg.gold + input.gold,
      claimedQuestIds: [...rpg.claimedQuestIds, input.questId],
    },
  };
}

/**
 * Every fight the profile is carrying, by `battleId`.
 *
 * The key is a *battle* id and not a boss id. A boss stores under its own id;
 * a concept-scale encounter stores under `site:<conceptId>`, the way boss and
 * quest reward claims share `claimedQuestIds` behind a `boss:` prefix. One map,
 * one merge rule (`mergeBattles`), and no second copy of the "keep the fight
 * that got further" logic for the smaller fights.
 *
 * Empty for a profile predating any of it.
 */
export const battlesOf = (profile: LearnerProfile): Readonly<Record<string, BossBattleRecord>> =>
  profile.rpg?.battles ?? {};

/**
 * Appends one graded answer to a fight.
 *
 * The whole of what a fight in progress needs to survive a reload: the
 * questions themselves are re-derived from (learner, definition, attempt) by
 * `startEncounter`, so the ordered list of how each one went is enough to
 * replay the fight exactly as the student left it.
 *
 * Malformed input must never arrive here — the caller checks the status, the
 * encounter reducer refuses it, and `applyAttempt` refuses it — so an answer in
 * this list is always an answer the student really gave.
 *
 * `attemptIndex` is passed in rather than read, and it is what keeps a stale
 * screen honest: an answer from an abandoned attempt cannot append itself to
 * a newer one, and a newer attempt restarts the list rather than continuing it.
 */
export function recordBattleAnswer(
  profile: LearnerProfile,
  input: { battleId: string; attemptIndex: number; answer: BossAnswer },
): LearnerProfile {
  const rpg = rpgOf(profile);
  const battles = rpg.battles ?? {};
  const current = battles[input.battleId];

  // An older attempt writing into a newer one would corrupt the replay: the
  // answers would be scored against questions from a fight nobody fought.
  if (current && current.attemptIndex > input.attemptIndex) return profile;

  const answers = current && current.attemptIndex === input.attemptIndex ? current.answers : [];

  return {
    ...profile,
    rpg: {
      ...rpg,
      battles: {
        ...battles,
        [input.battleId]: { attemptIndex: input.attemptIndex, answers: [...answers, input.answer] },
      },
    },
  };
}

/**
 * Starts the fight again, from the top and with fresh problems.
 *
 * The next `attemptIndex` is the entire mechanism: the seed of every question
 * derives from it, so a retry is a genuinely different fight rather than the
 * same four questions with the answers already known. Nothing is deducted and
 * nothing is recorded about the attempt that just ended — losing a fight costs
 * nothing anywhere in the profile (`docs/rpg.md` §12).
 */
export function resetBattle(profile: LearnerProfile, input: { battleId: string }): LearnerProfile {
  const rpg = rpgOf(profile);
  const battles = rpg.battles ?? {};
  const current = battles[input.battleId];
  return {
    ...profile,
    rpg: {
      ...rpg,
      battles: { ...battles, [input.battleId]: { attemptIndex: (current?.attemptIndex ?? 0) + 1, answers: [] } },
    },
  };
}

/** Journal entry ids the student has already read. Empty for a profile predating the journal. */
export const journalSeenOf = (profile: LearnerProfile): readonly string[] => profile.rpg?.journalSeen ?? [];

/**
 * Marks Adventure Journal entries as read.
 *
 * Idempotent by construction, exactly like `claimQuestReward`: the stored value
 * is a set, adding an id already in it changes nothing, and a profile with
 * nothing new to record is returned *unchanged* rather than rebuilt — the
 * journal page marks on mount, and a fresh object every render would be a
 * write, a save and a sync for a page nobody touched.
 *
 * This is the only thing the journal stores. Entries themselves are derived
 * (`journalEntries` in `@yuan-math/world`), and nothing here goes near
 * `concepts`, `attempts` or `stats`: having read about a mistake is not
 * evidence about mathematics, and must never be mistaken for fixing it.
 */
export function markJournalSeen(profile: LearnerProfile, input: { entryIds: readonly string[] }): LearnerProfile {
  const rpg = rpgOf(profile);
  const seen = rpg.journalSeen ?? [];
  const fresh = input.entryIds.filter((id) => !seen.includes(id));
  if (fresh.length === 0) return profile;
  return { ...profile, rpg: { ...rpg, journalSeen: [...seen, ...new Set(fresh)] } };
}

/** Misconceptions the student has correctly named. Empty for a profile predating the encounter. */
export const namedTricksOf = (profile: LearnerProfile): readonly string[] => profile.rpg?.namedTrickIds ?? [];

/**
 * Records that the student named a mistake correctly, in an encounter's
 * closing beat.
 *
 * Idempotent by construction, exactly like `markJournalSeen`: the stored value
 * is a set, and a profile with nothing new to record is returned *unchanged*
 * rather than rebuilt.
 *
 * **Nothing here goes near `concepts`, `attempts`, `stats` or `xp`, and that is
 * the whole design.** A seal is not an exercise: it asks the student to
 * recognise what went wrong, and recognising a mistake is not the same as
 * having stopped making it. Paying XP for it would make talking about
 * mathematics worth as much as doing it; moving mastery for it would let a
 * student claim a concept by correctly describing their own confusion. It is
 * the same line `markJournalSeen` draws one page over — having read about a
 * mistake is not evidence about mathematics.
 */
export function recordTrickNamed(profile: LearnerProfile, input: { misconceptionId: string }): LearnerProfile {
  const rpg = rpgOf(profile);
  const named = rpg.namedTrickIds ?? [];
  if (named.includes(input.misconceptionId)) return profile;
  return { ...profile, rpg: { ...rpg, namedTrickIds: [...named, input.misconceptionId] } };
}

/** Records a boss as beaten. Idempotent — a re-fought boss is already cleared. */
export function recordBossClear(profile: LearnerProfile, input: { bossId: string }): LearnerProfile {
  const rpg = rpgOf(profile);
  if (rpg.clearedBossIds.includes(input.bossId)) return profile;
  return { ...profile, rpg: { ...rpg, clearedBossIds: [...rpg.clearedBossIds, input.bossId] } };
}

/**
 * Spends gold, refusing overdraft: an insufficient balance returns the profile
 * unchanged rather than ever going negative. Buying an item already owned is
 * also refused — a cosmetic is owned once, and paying for it twice would just
 * be losing gold.
 */
export function spendGold(
  profile: LearnerProfile,
  input: { amount: number; itemId?: string },
): LearnerProfile {
  const rpg = rpgOf(profile);
  // A non-positive amount is a caller bug, not a purchase; minting gold by
  // "spending" a negative number must be impossible.
  if (!Number.isFinite(input.amount) || input.amount <= 0) return profile;
  if (rpg.gold < input.amount) return profile;
  if (input.itemId !== undefined && rpg.inventory.includes(input.itemId)) return profile;
  return {
    ...profile,
    rpg: {
      ...rpg,
      gold: rpg.gold - input.amount,
      ...(input.itemId !== undefined ? { inventory: [...rpg.inventory, input.itemId] } : {}),
    },
  };
}

/* -------------------------------------------------------------------------- */
/* Cosmetics                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Puts a cosmetic in the student's inventory without charging for it.
 *
 * The other half of `spendGold`: a bought cosmetic is paid for and appended in
 * one transaction, and an *earned* one is appended with nothing deducted. Both
 * refuse a duplicate, so owning a thing twice is not a state this profile can
 * reach however many times a button is tapped.
 *
 * Deliberately unconditional about *why*: whether the student has earned it is
 * a question about item definitions, which live in `content/` — `claimItem` in
 * `@yuan-math/world` asks it and calls this, the same way `settleBossVictory`
 * wraps `recordBossClear`. Nothing here knows what an item is.
 */
export function grantItem(profile: LearnerProfile, input: { itemId: string }): LearnerProfile {
  const rpg = rpgOf(profile);
  if (rpg.inventory.includes(input.itemId)) return profile;
  return { ...profile, rpg: { ...rpg, inventory: [...rpg.inventory, input.itemId] } };
}

/**
 * Records something the student built on the map.
 *
 * Deliberately unconditional about *whether they may*: that is a question about
 * terrain and about how many questions they have answered, and both live above
 * this package — `placeBuild` in `@yuan-math/world` asks it and calls this, the
 * same way `claimItem` wraps `grantItem`. Nothing here knows what a bridge is.
 *
 * One build to a tile, and the first one wins. Building over somebody's own
 * earlier work would make the reducer order-dependent, and a merge between two
 * devices would then disagree with itself about which house is there.
 */
export function recordBuild(
  profile: LearnerProfile,
  input: { kind: string; x: number; y: number; at: number },
): LearnerProfile {
  const rpg = rpgOf(profile);
  const builds = rpg.builds ?? [];
  if (builds.some((build) => build.x === input.x && build.y === input.y)) return profile;
  return { ...profile, rpg: { ...rpg, builds: [...builds, { ...input }] } };
}

/**
 * Wears an owned cosmetic in a slot, replacing whatever was there.
 *
 * Refuses anything not in `inventory`, because `equipped` is a projection of
 * what the student owns and a slot holding an unowned id would draw an avatar
 * out of nothing. The slot is a plain string here for the same reason the item
 * id is: the vocabulary of slots belongs to the item definitions above.
 */
export function equipCosmetic(
  profile: LearnerProfile,
  input: { slot: string; itemId: string },
): LearnerProfile {
  const rpg = rpgOf(profile);
  if (!rpg.inventory.includes(input.itemId)) return profile;
  if (rpg.equipped[input.slot] === input.itemId) return profile;
  return { ...profile, rpg: { ...rpg, equipped: { ...rpg.equipped, [input.slot]: input.itemId } } };
}

/** Takes whatever is in a slot off. A no-op on an empty slot, so it never writes. */
export function unequipCosmetic(profile: LearnerProfile, input: { slot: string }): LearnerProfile {
  const rpg = rpgOf(profile);
  if (rpg.equipped[input.slot] === undefined) return profile;
  const equipped = { ...rpg.equipped };
  delete equipped[input.slot];
  return { ...profile, rpg: { ...rpg, equipped } };
}
